/**
 * Moving one of a block's activities to another block that day, or to a new
 * block of its own (section 9.4; ADR 0060). It takes that block's client.
 * The popup's More row opens it in the popup's right pane (ADR 0193): which
 * activity, then where to.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import type { Activity } from "../../api/types";
import { useSession } from "../../app/session-context";
import { formatClockRange } from "../../lib/dates";
import { type BlockView, activityRange, activityTitle, movedIn } from "./model";

const NEW_BLOCK = "new";
const secondary =
  "h-9 cursor-pointer self-start rounded-pill border border-line bg-panel px-3.5 text-[13px] font-semibold hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    if (error.status === 409 && error.detail) {
      return `Couldn't move it: ${error.detail}.`;
    }
    return error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function MoveActivity({
  activity,
  title,
  targets,
  timeZone,
  movedIn = false,
  onDone,
}: {
  activity: Activity;
  /** The activity's name, for the form's label. */
  title: string;
  /** The day's other blocks that can take it. */
  targets: readonly BlockView[];
  timeZone: string;
  /** An email moved in from an earlier day: never a block of its own (ADR 0131). */
  movedIn?: boolean;
  /** Closes the form; once it's moved, with what happened, when the day shows it (#247). */
  onDone: (message?: string) => void;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const selectId = useId();
  const [target, setTarget] = useState("");
  const move = useMutation({
    mutationFn: (to: string | null) => api.moveActivity(activity.id, to),
    onSuccess: async (_, to) => {
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      onDone(`Moved ${title} to ${targets.find((b) => b.id === to)?.title ?? "a new block"}.`);
      // The Log form's preview (title, notes, hours) is built from the block's
      // activities: ask for it again, or it shows the old block (#224).
      await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    },
  });

  return (
    <form
      aria-label={`Move ${title}`}
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (target !== "") {
          move.mutate(target === NEW_BLOCK ? null : target);
        }
      }}
    >
      <label htmlFor={selectId} className="text-xs font-semibold text-muted">
        Move to
      </label>
      <select
        id={selectId}
        value={target}
        onChange={(event) => {
          setTarget(event.target.value);
        }}
        className="h-10 w-full rounded-input border border-line bg-card px-2 text-[13.5px] text-text"
      >
        <option value="">Choose a block</option>
        {movedIn ? null : <option value={NEW_BLOCK}>A new block of its own</option>}
        {targets.map((block) => (
          <option key={block.id} value={block.id}>
            {`${block.title} · ${formatClockRange(block.startedAt, block.endedAt, timeZone)}`}
          </option>
        ))}
      </select>
      {move.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(move.error)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={target === "" || move.isPending}
          className="h-9 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {move.isPending ? "Moving…" : "Move"}
        </button>
        <button
          type="button"
          onClick={() => {
            onDone();
          }}
          className={secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The popup's Move pane (ADR 0193): which of the block's activities, the
 * first to start with, then MoveActivity's form for it. Cancel goes back.
 */
export function MoveAnActivity({
  block,
  targets,
  timeZone,
  onDone,
}: {
  block: BlockView;
  targets: readonly BlockView[];
  timeZone: string;
  /** Moved, with what happened; or cancelled, with nothing. */
  onDone: (message?: string) => void;
}) {
  const selectId = useId();
  const [chosenId, setChosenId] = useState(block.activities[0]?.id ?? "");
  const chosen = block.activities.find((a) => a.id === chosenId) ?? block.activities[0];
  if (chosen === undefined) {
    return null;
  }
  const title = activityTitle(chosen);
  return (
    <>
      <div className="flex flex-col gap-[5px]">
        <label htmlFor={selectId} className="text-xs font-semibold text-muted">
          Activity
        </label>
        <select
          id={selectId}
          value={chosen.id}
          onChange={(event) => {
            setChosenId(event.target.value);
          }}
          className="h-10 w-full rounded-input border border-line bg-card px-2 text-[13.5px] text-text"
        >
          {block.activities.map((activity) => (
            <option key={activity.id} value={activity.id}>
              {`${activityRange(activity, block, timeZone)} · ${activityTitle(activity)}`}
            </option>
          ))}
        </select>
      </div>
      <MoveActivity
        // Another activity chosen: its own form, afresh.
        key={chosen.id}
        activity={chosen}
        title={title}
        targets={targets}
        timeZone={timeZone}
        movedIn={movedIn(chosen, block, timeZone)}
        onDone={onDone}
      />
    </>
  );
}
